<script>
// @ts-check

import BaseErrors from '@/components/base/BaseErrors.vue'
import { id } from '@/core/props'
import * as _ from 'lodash-es'
import { nextTick } from 'vue'

/**
 * Deprecated searchable select component
 * @deprecated Use VAutocomplete instead
 */

export default {
  name: 'BaseSearchableSelect',
  components: {
    BaseErrors
  },
  props: {
    id,
    modelValue: {
      type: [String, Number],
      default: null
    },
    label: {
      type: String,
      required: true
    },
    options: {
      type: [Array, Function],
      required: true
    },
    errors: {
      /** @type {import('vue').PropType<string[]|null>} */
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    valuePath: {
      type: String,
      default: 'value'
    },
    labelPath: {
      type: String,
      default: 'label'
    },
    maxHeight: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    required: {
      type: Boolean
    }
  },
  emits: ['update:modelValue'],
  data: () => ({
    open: false,
    allOptions: [],
    mutableOptions: [],
    input: '',
    inputHasFocus: false
  }),
  computed: {
    active() {
      return this.inputHasFocus || this.input
    },
    hasErrors() {
      return this.errors.length > 0
    },
    translatedOptions() {
      return this.allOptions.map((option) => {
        if (option.translate) {
          return {
            ...option,
            label: this.$t(option.label, option.variables)
          }
        }
        return option
      })
    },
    filteredOptions() {
      const search = this.input.toString().toLowerCase()

      if (this.input !== '') {
        return _.filter(this.translatedOptions, function (option) {
          const { value } = option
          let valueMatch = false
          if (typeof value === 'string') {
            valueMatch = value.toLowerCase().includes(search)
          }

          return valueMatch || option.label.toLowerCase().includes(search)
        })
      }
      return this.translatedOptions
    },
    visualValue() {
      return this.input
    }
  },
  watch: {
    options: {
      deep: true,
      handler(newOptions) {
        if (typeof newOptions === 'function') {
          newOptions().then((res) => {
            this.allOptions = res
            this.handleOptions()
          })
        } else {
          this.allOptions = newOptions
          this.handleOptions()
        }
      }
    },
    disabled(isDisabled) {
      if (isDisabled) {
        this.open = false
      }
    },
    open(open) {
      if (open) {
        document.addEventListener('click', this.onClickOutside)
      } else {
        document.removeEventListener('click', this.onClickOutside)
      }
    }
  },
  unmounted() {
    document.removeEventListener('click', this.onClickOutside)
  },
  created() {
    if (typeof this.options === 'function') {
      this.options().then((res) => {
        this.allOptions = res
        this.handleOptions()
      })
    } else {
      this.allOptions = this.options
      this.handleOptions()
    }

    this.initInput()

    if (this.translatedOptions.length) {
      return
    }

    this.translatedOptionsWatcher = this.$watch(
      'translatedOptions',
      (newTranslatedOptions) => {
        if (newTranslatedOptions.length) {
          this.initInput()
          // Unwatch
          this.translatedOptionsWatcher()
        }
      }
    )
  },
  methods: {
    initInput() {
      if (this.modelValue) {
        const selectedOption = this.translatedOptions.find(
          (option) => option.value === this.modelValue
        )
        if (selectedOption) {
          this.input = selectedOption.label
          return
        }
      }
      this.input = ''
    },
    onClearClick(event) {
      event.stopPropagation()
      this.clear(true)
    },
    clear(clearInput) {
      this.inputHasFocus = false
      this.open = false
      if (clearInput) {
        this.input = ''
        this.$emit('update:modelValue', null)
      }
    },
    toggleList() {
      if (this.disabled) {
        return
      }
      this.open = !this.open
    },
    handleOptions() {
      this.allOptions = this.allOptions.map((option) => ({
        value: _.get(option, this.valuePath),
        label: _.get(option, this.labelPath).toString(),
        variables: option.variables || [],
        translate: option.translate
      }))

      if (!this.allOptions.length || !this.modelValue) {
        return
      }

      if (!this.allOptions.find((option) => option.value === this.modelValue)) {
        this.clear(true)
      }
    },
    selectOption(event, option) {
      this.input = option.label
      this.$emit('update:modelValue', option.value)
      this.open = !this.open
    },
    filter(e) {
      this.input = e.target.value

      nextTick().then(() => {
        if (this.filteredOptions.length > 0) {
          this.open = true
        }
      })
    },
    onClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.clear(false)
      }
    }
  }
}
</script>

<template>
  <div class="searchable-select relative w-full">
    <label class="input-label" :for="id">
      {{ label }}
      <span v-if="required" class="input-required">*</span>
    </label>
    <div
      class="search-field relative fill-current"
      :class="{
        open,
        loading
      }"
    >
      <input
        :id
        tabindex="0"
        :disabled="disabled || loading"
        class="text-input truncate pr-10"
        :class="{
          'pr-20': loading,
          'has-error': hasErrors
        }"
        type="text"
        :placeholder="placeholder.length ? placeholder : $t('general.search')"
        autocomplete="off"
        :value="visualValue"
        @input="filter"
        @focus="inputHasFocus = true"
        @click="toggleList"
      />
      <div
        v-if="!disabled"
        class="absolute inset-y-0 right-0 flex items-center pr-4"
        :class="{
          'pointer-events-none': !visualValue
        }"
      >
        <button
          v-if="visualValue !== ''"
          type="button"
          class="flex items-center hover:text-primary"
          :aria-label="$t('general.clear_selection')"
          @click="onClearClick"
        >
          <VIcon icon="$close" :size="18" role="presentation" />
        </button>
        <VIcon v-else icon="$magnify" size="1.125rem" class="mb-1" />
      </div>
    </div>

    <ul
      v-show="open"
      tabindex="0"
      class="options-list tab absolute z-10 mt-2 w-full overflow-x-hidden overflow-y-auto"
      :style="{ 'max-height': maxHeight }"
      role="listbox"
    >
      <button
        v-for="(option, i) in filteredOptions"
        :key="i"
        role="option"
        type="button"
        class="list-item w-full text-left hover:bg-secondary-200"
        @click.prevent="(evt) => selectOption(evt, option)"
      >
        {{ option.label }}
      </button>
      <li v-if="filteredOptions.length < 1" class="list-item">
        {{ $t('form.select.no_match') }}
      </li>
    </ul>

    <BaseErrors class="mt-2" :errors :label />

    <slot />
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.search-field {
  @apply w-full;

  &.loading::after {
    display: block;
    content: '';
    height: 1rem;
    width: 1rem;
    position: absolute;
    top: calc(50% - (1rem / 2));
    right: 3.25rem;
    border: 2px solid var(--color-primary);
    border-radius: 9999px;
    border-color: transparent transparent var(--color-primary)
      var(--color-primary);
    animation: donut-spin 0.9s infinite linear;
  }
}

input.text-input {
  @apply w-full mb-0;
  @apply bg-white rounded-l;

  &:disabled {
    @apply bg-secondary-200;
  }
}

.open {
  @apply rounded-b-none;
  @apply bg-transparent;
}

.options-list {
  @apply border border-secondary-200 max-h-72 rounded-sm bg-white shadow-md;
}

.list-item {
  @apply px-4 py-2 font-secondary;
  min-height: 2.4rem;
}
</style>
